import { useMemo } from "react";
import { UIFlightData, UIOrder, UIUnifiedPriceBreakdownItem } from "@flights/types";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { localizationUtils, priceBreakdownUtils, ryanairUtils } from "@flights/web-api-utils-universal";
import { useFlightOfferWithDiscount } from "hooks/useFlightOfferWithDiscount";
import usePointOfSale from "hooks/usePointOfSale";
import { transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import uniq from "lodash/uniq";
import { MpProduct } from "@bookingcom/mp-flights";
import { transfromMultiProductsToOrderExtras } from "./transforms/multiProducts";
import { type BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "hooks/useRouteName";

export type UnifiedPriceBreakdownOrderControllerProps = {
  order?: UIOrder;
  flight?: UIFlightData; // flights_web_unified_price_breakdown
  multiProducts?: MpProduct[];
};

type CopyFuncParams = Parameters<typeof priceBreakdownUtils.getExtraCopy>;
type POSType = CopyFuncParams[3]["pointOfSale"];

export function useUnifiedPriceBreakdownOrderController(props: UnifiedPriceBreakdownOrderControllerProps) {
  const { order, flight, multiProducts } = props;
  const i18n = useI18n();
  const ipCountry = usePointOfSale();
  const routeName = useRouteName();
  const unifiedPriceBreakdown = useMemo(() => order?.unifiedPriceBreakdown, [order?.unifiedPriceBreakdown]);
  const segments = useMemo(() => order?.airOrder?.flightSegments, [order?.airOrder?.flightSegments]);
  const instalmentsFees = order?.totalPrice?.instalmentsFees;

  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );

  const baseOptions = useMemo(
    () => ({
      pointOfSale: (ipCountry || "") as POSType,
      isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
      segments: segments || []
    }),
    [ipCountry, isHungaryDepartingRyanAir, segments]
  );

  const addedItems: UIUnifiedPriceBreakdownItem[] = useMemo(() => {
    const extras = transfromMultiProductsToOrderExtras(multiProducts);
    const allItems = priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(i18n, extras, baseOptions);

    if (!instalmentsFees) return allItems;

    const installmentItem: UIUnifiedPriceBreakdownItem = {
      id: "dynamic-instalments-fees",
      scope: "FEE",
      title: i18n.trans(t("flights_price_breakdown_instalment_label")),
      price: instalmentsFees,
      items: []
    };

    allItems.push(installmentItem);

    return allItems;
  }, [i18n, instalmentsFees, baseOptions, multiProducts]);

  const disclaimers = useMemo(() => {
    return uniq(transformDisclaimersForPriceBreakdown(order?.totalPrice || flight?.priceBreakdown));
  }, [order, flight]);

  const { isWithDiscount } = useFlightOfferWithDiscount(flight); // flights_web_unified_price_breakdown

  const isRyanair = useMemo(() => ryanairUtils.isRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    expandedByDefault,
    disclaimers,
    isWithDiscount, // flights_web_unified_price_breakdown
    addedItems,
    routeName
  };
}
